import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useNotifications } from '../contexts/NotificationContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { useConfirm } from './ConfirmModal';
import './TownDetailsModal.css';

interface Town {
  id: number;
  name: string;
  description: string;
  townType: string;
  worldX: number;
  worldY: number;
  bossName: string;
  isLiberated: boolean;
  standing: number;
  level: number;
  lastActivityDate: string | null;
  canFastTravel: boolean;
}

interface Quest {
  type: string;
  description: string;
  standingReward: number;
  requiredCount: number;
  requiredMaterialType?: string;
}

interface Bonus {
  slot: number;
  tier: number;
  requiredLevel: number;
  bonusType: string;
  bonusName: string;
  description: string;
  bonusValue: number;
  isActive: boolean;
  isLocked: boolean;
}

interface BossSpawn {
  monsterName: string;
  monsterLevel: number;
  isDifficulty: string;
}

interface TownProgress {
  isLiberated: boolean;
  canFastTravel: boolean;
  standing: number;
  level: number;
}

interface TownDetails {
  town: {
    name: string;
    description: string;
    townType: string;
    bossName: string;
  };
  progress: TownProgress;
  bossSpawns: BossSpawn[];
  availableQuests: Quest[];
  bonuses: Bonus[];
}

interface QuestProgress {
  combat: number;
  eliteCombat: number;
  crafting: number;
}

interface TownDetailsModalProps {
  townId: number;
  onClose: () => void;
  onFastTravel?: (townId: number) => void;
}

const TownDetailsModal: React.FC<TownDetailsModalProps> = ({ townId, onClose, onFastTravel }) => {
  const { checkAndStartTutorial } = useTutorial();
  const { addNotification } = useNotifications();
  const { confirm, confirmDialog } = useConfirm();
  const [town, setTown] = useState<TownDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completingQuest, setCompletingQuest] = useState(false);
  const [questProgress, setQuestProgress] = useState<QuestProgress | null>(null);

  useEffect(() => {
    loadTownDetails();
    loadQuestProgress();
  }, [townId]);

  useEffect(() => {
    if (town && !loading) {
      checkAndStartTutorial('town-system-basics');
    }
  }, [town, loading, checkAndStartTutorial]);

  const loadQuestProgress = async () => {
    try {
      const response = await makeAuthenticatedRequest('/town/quest-progress');
      if (response.ok) {
        const data = await response.json();
        setQuestProgress(data);
      }
    } catch (err) {
      logger.error('Failed to load quest progress:', err);
    }
  };

  const loadTownDetails = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`/town/${townId}`);

      if (response.ok) {
        const data = await response.json();
        setTown(data);
      } else {
        setError('Failed to load town details');
      }
    } catch (err) {
      setError('Error loading town details');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteQuest = async (quest: Quest) => {
    if (!town) return;
    if (!town.progress.isLiberated) {
      setError('Town must be liberated first! Defeat the boss to liberate this town.');
      return;
    }

    try {
      setError('');
      setCompletingQuest(true);
      const response = await makeAuthenticatedRequest(`/town/${townId}/quest/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questType: quest.type,
          questDescription: quest.description,
          standingReward: quest.standingReward,
          requiredCount: quest.requiredCount,
          requiredMaterialType: quest.requiredMaterialType
        })
      });

      if (response.ok) {
        const result = await response.json();
        addNotification({
          type: 'quest',
          title: 'Quest Complete!',
          message: `+${result.standingGained} standing — New Standing: ${result.newStanding}, Town Level: ${result.newLevel}`,
        });
        await loadTownDetails();
        await loadQuestProgress();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to complete quest');
      }
    } catch (err) {
      setError('Error completing quest');
      logger.error(err);
    } finally {
      setCompletingQuest(false);
    }
  };

  const handleFastTravel = async () => {
    if (!onFastTravel || !town) return;

    if (!town.progress.canFastTravel) {
      setError('Town must be liberated (Level >= 1) to fast travel');
      return;
    }

    const confirmed = await confirm({
      title: 'Fast Travel',
      message: `Fast travel to ${town.town.name}? This will cost 25% of normal travel points.`,
      confirmText: 'Travel',
    });
    if (confirmed) {
      onFastTravel(townId);
    }
  };

  const getTownIcon = (type: string) => {
    switch (type) {
      case 'Mining':  return '⛏️';
      case 'Forest':  return '🌲';
      case 'Trading': return '🏪';
      case 'Military':return '⚔️';
      case 'Magical': return '🔮';
      default:        return '🏰';
    }
  };

  const getStandingColor = (standing: number) => {
    if (standing >= 900) return '#c49a2a';
    if (standing >= 700) return '#9d4edd';
    if (standing >= 500) return '#42a5f5';
    if (standing >= 300) return '#3da86e';
    if (standing >= 100) return '#e6b84a';
    return '#d94444';
  };

  const getQuestProgress = (questType: string): number => {
    if (!questProgress) return 0;
    switch (questType) {
      case 'Combat':      return questProgress.combat || 0;
      case 'EliteCombat': return questProgress.eliteCombat || 0;
      case 'Crafting':    return questProgress.crafting || 0;
      default:            return 0;
    }
  };

  if (loading) {
    return (
      <div className="town-modal-overlay" onClick={onClose}>
        <div className="town-modal" onClick={(e) => e.stopPropagation()}>
          <div className="tm-content">
            <p style={{ color: 'var(--text-dim)', fontFamily: "'Exo 2', sans-serif" }}>Loading town details…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !town) {
    return (
      <div className="town-modal-overlay" onClick={onClose}>
        <div className="town-modal" onClick={(e) => e.stopPropagation()}>
          <div className="tm-content">
            <div className="tm-error">{error}</div>
            <span className="tm-cancel-btn" onClick={onClose} role="button" tabIndex={0}>Close</span>
          </div>
        </div>
      </div>
    );
  }

  if (!town) return null;

  return (
    <div className="town-modal-overlay" onClick={onClose}>
      {confirmDialog}
      <div className="town-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ──────────────────────────── */}
        <div className="tm-header">
          <h3>{getTownIcon(town.town.townType)} {town.town.name}</h3>
          <div className="tm-header-meta">
            <span className={`tm-badge ${town.progress.isLiberated ? 'tm-badge--green' : 'tm-badge--red'}`}>
              {town.progress.isLiberated ? 'Liberated' : 'Occupied'}
            </span>
            <span className="tm-badge tm-badge--gold">{town.town.townType}</span>
            {town.progress.isLiberated && (
              <span className="tm-badge tm-badge--blue">Level {town.progress.level}</span>
            )}
          </div>
          <span className="tm-close-btn" onClick={onClose} role="button" tabIndex={0}>×</span>
        </div>

        {/* ── Content ─────────────────────────── */}
        <div className="tm-content">
          {error && <div className="tm-error">{error}</div>}

          <p className="tm-description">{town.town.description}</p>

          {/* Town Progress */}
          <div className="tm-progress-section">
            <div className="tm-section-header">Town Progress</div>

            {town.progress.canFastTravel && onFastTravel && (
              <span className="tm-fast-travel-btn" onClick={handleFastTravel} role="button" tabIndex={0}>
                Fast Travel
              </span>
            )}

            {!town.progress.isLiberated ? (
              <>
                <div className="tm-not-liberated">
                  <div className="tm-not-liberated-title">⚔ Town Under Enemy Control</div>
                  <p>Defeat <strong>{town.town.bossName}</strong> to liberate this town.</p>
                </div>

                {town.bossSpawns && town.bossSpawns.length > 0 ? (
                  <>
                    <div className="tm-section-header tm-section-header--red">Boss Available</div>
                    {town.bossSpawns.map((boss: BossSpawn, idx: number) => (
                      <div key={idx} className="tm-boss-spawn-card">
                        <span className="tm-boss-name">{boss.monsterName}</span>
                        <span className="tm-boss-meta">Lv {boss.monsterLevel} · {boss.isDifficulty}</span>
                      </div>
                    ))}
                    <p style={{ fontSize: '12px', color: 'var(--text-mt)', marginTop: '8px', fontFamily: "'Exo 2', sans-serif" }}>
                      Battle this enemy on the world map to free the town.
                    </p>
                  </>
                ) : (
                  <p className="tm-no-boss-warning">⚠ No boss spawns available. Explore nearby tiles to trigger encounters!</p>
                )}
              </>
            ) : (
              <div className="tm-info-grid">
                <div className="tm-info-cell">
                  <span className="label">Standing</span>
                  <span className="value">{town.progress.standing} / 1000</span>
                </div>
                <div className="tm-info-cell">
                  <span className="label">Level</span>
                  <span className="value">{town.progress.level}</span>
                </div>
                <div className="tm-info-cell tm-info-cell--full" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div className="tm-standing-bar">
                    <div
                      className="tm-standing-fill"
                      style={{
                        width: `${(town.progress.standing / 1000) * 100}%`,
                        background: getStandingColor(town.progress.standing)
                      }}
                    />
                  </div>
                  <p className="tm-next-bonus">
                    {town.progress.level < 9
                      ? `Next bonus at Level ${town.progress.level + 1} — ${100 * (town.progress.level + 1) - town.progress.standing} standing needed`
                      : 'Max Level Reached'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Quests */}
          {town.progress.isLiberated && (
            <div className="tm-quests-section">
              <div className="tm-section-header tm-section-header--green">Available Quests</div>
              <div className="tm-quest-list">
                {town.availableQuests.map((quest: Quest, idx: number) => {
                  const currentProgress = getQuestProgress(quest.type);
                  const hasEnoughProgress = currentProgress >= quest.requiredCount;

                  return (
                    <div key={idx} className="tm-quest-card">
                      <div className="tm-quest-header">
                        <span className="tm-quest-type">{quest.type}</span>
                        <span className="tm-quest-reward">+{quest.standingReward} Standing</span>
                      </div>
                      <p className="tm-quest-description">{quest.description}</p>
                      {quest.type !== 'Gathering' && quest.type !== 'Donation' ? (
                        <p className={`tm-quest-progress ${hasEnoughProgress ? 'tm-quest-progress--ready' : 'tm-quest-progress--pending'}`}>
                          Progress: {currentProgress}/{quest.requiredCount}{hasEnoughProgress && ' ✓'}
                        </p>
                      ) : (
                        <p className="tm-quest-progress tm-quest-progress--pending">
                          {quest.type === 'Donation'
                            ? `Requires ${quest.requiredCount} ${quest.requiredMaterialType ?? 'Any Materials'} in inventory — consumed on completion`
                            : `Requires ${quest.requiredCount} ${quest.requiredMaterialType ?? 'Any Materials'} in inventory — materials are kept`}
                        </p>
                      )}
                      <button
                        className="tm-complete-quest-btn"
                        onClick={() => handleCompleteQuest(quest)}
                        disabled={completingQuest}
                      >
                        {completingQuest ? 'Completing…' : 'Complete Quest'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bonuses */}
          <div className="tm-bonuses-section">
            <div className="tm-section-header tm-section-header--gold">Town Bonuses</div>
            <div className="tm-bonus-grid">
              {[1, 2, 3].map(slot => {
                const slotBonuses = town.bonuses.filter((b: Bonus) => b.slot === slot);
                return (
                  <div key={slot} className="tm-bonus-slot">
                    <div className="tm-bonus-slot-title">Slot {slot}</div>
                    {slotBonuses.map((bonus: Bonus) => (
                      <div
                        key={`${bonus.slot}-${bonus.tier}`}
                        className={`tm-bonus-tier${bonus.isActive ? ' tm-bonus-tier--active' : ' tm-bonus-tier--locked'}`}
                      >
                        <div className="tm-bonus-header">
                          <span className="tm-bonus-name">{bonus.bonusName}</span>
                          <span className="tm-bonus-level">Lv {bonus.requiredLevel}</span>
                        </div>
                        <p className="tm-bonus-description">{bonus.description}</p>
                        {bonus.isLocked && (
                          <span className="tm-bonus-status-badge tm-bonus-status-badge--locked">🔒 Locked</span>
                        )}
                        {bonus.isActive && (
                          <span className="tm-bonus-status-badge tm-bonus-status-badge--active">✓ Active</span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Actions ─────────────────────────── */}
        <div className="tm-actions">
          <span className="tm-cancel-btn" onClick={onClose} role="button" tabIndex={0}>Close</span>
        </div>
      </div>
    </div>
  );
};

export default TownDetailsModal;
